import React from "react";
import { useTranslation } from 'react-i18next';
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import styled, { keyframes } from 'styled-components';
import { makeStyles } from '@material-ui/core/styles';
import { trainingIndicatorResponseType } from "../common/type";
import { themeColors, basicColor } from "../common/colors";
import { useCustomMediaQuery } from "../common/useCustomMediaQuery";

type IndicatorChartTypeProps = {
  tiData: trainingIndicatorResponseType;
  tiDataType: "mext" | "tokushima";
  isExportingPDF?: boolean;
};

type IndicatorData = {
  value: number;
  orverValue: number;
  label: string;
  min: number;
  max: number;
};

type TrainingIndicatorData = {
  group?: string;
  span?: number;
  data: IndicatorData[];
};

interface TITableRowProps extends TrainingIndicatorData {
  isExportingPDF?: boolean;
};

// Material-UIのスタイル設定
const useStyles = makeStyles({
  tableHeadStyle: {
    textAlign: "center",
    borderRight: "solid 1px #fff",
    lineHeight: "1rem",
    padding: "10px 0",
  },
  tableHeadSpStyle: {
    textAlign: "center",
    borderRight: "solid 1px #fff",
    lineHeight: "1rem",
    padding: "10px 0",
    fontSize: '10px',
  },
  tableCellStyle: {
    padding: "4px 16px",
  },
  tableCellSpStyle: {
    padding: "4px 16px",
    fontSize: '10px',
  },
  tableCellChartStyle: {
    position: 'relative',
    padding: "0",
    overflow: "hidden",
  }
});

const expand = (width: string) => keyframes`
  from {
    width: 0;
  }
  to {
    width: ${width};
  }
`;

type BarChartStyleProps = {
  width: string;
};

const BarChartStyle = styled.div<BarChartStyleProps>`
  position: absolute;
  top: calc(50% - 10px); 
  left:0;
  width: ${props => props.width};
  height: 20px;
  justify-content: flex-start;
`;

const BarChartOverStyle = styled.div<BarChartStyleProps>`
  position: absolute;
  top: calc(50% - 10px);
  width: 0;
  height: 20px;
  display: flex;
  text-align: left;
  color: #ffffff;
  animation: ${props => expand(props.width)} .3s linear forwards;
`;

const BarChartDataPointStyle = styled.div`
  position: absolute;
  top: calc(50% - .7em);
  left: 0;
  width: 100%;
  margin: 0 5px;
  opacity: 0;
  animation: showElement 0s forwards;

  @keyframes showElement {
  to {
    opacity: 1;
  }
}
`;

// PDFに出力したときに、グラフ要素の値のラベルの上下位置を調節する
const BarChartDataPointPDFStyle = styled.div`
  position: absolute;
  top: 3px;
  left: 0;
  width: 100%;
  margin: 0 5px;
}
`;

const IndicatorChart: React.FC<IndicatorChartTypeProps> = ({ tiData, isExportingPDF, tiDataType }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { isDesktop } = useCustomMediaQuery();
  const isDesktopStyle = isDesktop || isExportingPDF;
  const tableHeadClassName = isDesktopStyle ? classes.tableHeadStyle : classes.tableHeadSpStyle;
  const tableCellClassName = isDesktopStyle ? classes.tableCellStyle : classes.tableCellSpStyle;
  const ti = tiData.training_indicators;
  const max = Math.max(...ti);
  const min = Math.min(...ti);
  const indicators = ti.map(value => value > min ? [min, value - min] : [value, 0]);
  const sumWidth = 32;
  // テーブルヘッダー「標準」の幅は、trainingIndicatorDataの最小値の幅に合わせる
  const defaultLabelWidthValue = sumWidth * min / max;
  const tableHeadDefaultLabelWidth = `${defaultLabelWidthValue}vw`;
  const tableHeadAbilityLabelWidth = `${sumWidth - defaultLabelWidthValue}vw`;
  const tableHeadList = [
    { label: t("indicator.header_class"), width: "8vw" },
    { label: t("indicator.header_quality"), width: "20vw" },
    {
      label: t("indicator.header_standard"),
      width: tableHeadDefaultLabelWidth
    },
    {
      label: t("indicator.header_ability"),
      width: tableHeadAbilityLabelWidth
    },
  ];

  const createMextData = (max: number, min: number) => {
    return [
      {
        group: t("indicator.mext5_1"),
        span: 5,
        data: [
          { value: indicators[0][0], orverValue: indicators[0][1], label: t("indicator.mext_1"), min, max },
          { value: indicators[1][0], orverValue: indicators[1][1], label: t("indicator.mext_2"), min, max },
          { value: indicators[2][0], orverValue: indicators[2][1], label: t("indicator.mext_3"), min, max },
          { value: indicators[3][0], orverValue: indicators[3][1], label: t("indicator.mext_4"), min, max },
          { value: indicators[4][0], orverValue: indicators[4][1], label: t("indicator.mext_5"), min, max },
        ]
      },
      {
        group: t("indicator.mext5_2"),
        span: 4,
        data: [
          { value: indicators[5][0], orverValue: indicators[5][1], label: t("indicator.mext_6"), min, max },
          { value: indicators[6][0], orverValue: indicators[6][1], label: t("indicator.mext_7"), min, max },
          { value: indicators[7][0], orverValue: indicators[7][1], label: t("indicator.mext_8"), min, max },
          { value: indicators[8][0], orverValue: indicators[8][1], label: t("indicator.mext_9"), min, max },
        ]
      },
      {
        group: t("indicator.mext5_3"),
        span: 5,
        data: [
          { value: indicators[9][0], orverValue: indicators[9][1], label: t("indicator.mext_10"), min, max },
          { value: indicators[10][0], orverValue: indicators[10][1], label: t("indicator.mext_11"), min, max },
          { value: indicators[11][0], orverValue: indicators[11][1], label: t("indicator.mext_12"), min, max },
          { value: indicators[12][0], orverValue: indicators[12][1], label: t("indicator.mext_13"), min, max },
          { value: indicators[13][0], orverValue: indicators[13][1], label: t("indicator.mext_14"), min, max },
        ]
      },
      {
        group: t("indicator.mext5_4"),
        span: 1,
        data: [
          { value: indicators[14][0], orverValue: indicators[14][1], label: t("indicator.mext_15"), min, max },
        ]
      },
      {
        group: t("indicator.mext5_5"),
        span: 2,
        data: [
          { value: indicators[15][0], orverValue: indicators[15][1], label: t("indicator.mext_16"), min, max },
          { value: indicators[16][0], orverValue: indicators[16][1], label: t("indicator.mext_17"), min, max },
        ]
      },
    ];
  };

  const createTokushimaData = (max: number, min: number) => {
    return [
      {
        group: t("indicator.tokushima5_1"),
        span: 6,
        data: [
          { value: indicators[0][0], orverValue: indicators[0][1], label: t("indicator.tokushima_1"), min, max },
          { value: indicators[1][0], orverValue: indicators[1][1], label: t("indicator.tokushima_2"), min, max },
          { value: indicators[2][0], orverValue: indicators[2][1], label: t("indicator.tokushima_3"), min, max },
          { value: indicators[3][0], orverValue: indicators[3][1], label: t("indicator.tokushima_4"), min, max },
          { value: indicators[4][0], orverValue: indicators[4][1], label: t("indicator.tokushima_5"), min, max },
          { value: indicators[5][0], orverValue: indicators[5][1], label: t("indicator.tokushima_6"), min, max },
        ]
      },
      {
        group: t("indicator.tokushima5_2"),
        span: 4,
        data: [
          { value: indicators[6][0], orverValue: indicators[6][1], label: t("indicator.tokushima_7"), min, max },
          { value: indicators[7][0], orverValue: indicators[7][1], label: t("indicator.tokushima_8"), min, max },
          { value: indicators[8][0], orverValue: indicators[8][1], label: t("indicator.tokushima_9"), min, max },
          { value: indicators[9][0], orverValue: indicators[9][1], label: t("indicator.tokushima_10"), min, max },
        ]
      },
      {
        group: t("indicator.tokushima5_3"),
        span: 4,
        data: [
          { value: indicators[10][0], orverValue: indicators[10][1], label: t("indicator.tokushima_11"), min, max },
          { value: indicators[11][0], orverValue: indicators[11][1], label: t("indicator.tokushima_12"), min, max },
          { value: indicators[12][0], orverValue: indicators[12][1], label: t("indicator.tokushima_13"), min, max },
          { value: indicators[13][0], orverValue: indicators[13][1], label: t("indicator.tokushima_14"), min, max },
        ]
      },
      {
        group: t("indicator.tokushima5_4"),
        span: 4,
        data: [
          { value: indicators[14][0], orverValue: indicators[14][1], label: t("indicator.tokushima_15"), min, max },
          { value: indicators[15][0], orverValue: indicators[15][1], label: t("indicator.tokushima_16"), min, max },
          { value: indicators[16][0], orverValue: indicators[16][1], label: t("indicator.tokushima_17"), min, max },
          { value: indicators[17][0], orverValue: indicators[17][1], label: t("indicator.tokushima_18"), min, max },
        ]
      },
      {
        group: t("indicator.tokushima5_5"),
        span: 2,
        data: [
          { value: indicators[18][0], orverValue: indicators[18][1], label: t("indicator.tokushima_19"), min, max },
          { value: indicators[19][0], orverValue: indicators[19][1], label: t("indicator.tokushima_20"), min, max },
        ]
      },
    ];
  };
  const displayData = tiDataType === "tokushima" ? createTokushimaData(max, min) : createMextData(max, min);

  // tableのrowspanの都合上、各項目の最初の１行目はTITableFirstRowを使用
  const TITableFirstRow: React.FC<TITableRowProps> = ({ group, span, data, isExportingPDF }) => {
    const firstData = data[0];
    const leftStyle = { left: `${((firstData.value + firstData.orverValue) / firstData.max) * 50}%` };
    const dataPointValue = Math.round((firstData.value + firstData.orverValue) * 10) / 10;
    return (
      <TableRow key={group}>
        <TableCell className={tableCellClassName} rowSpan={span}>{group}</TableCell>
        <TableCell
          className={tableCellClassName}
          sx={{ borderBottom: data.length === 1 ? "1px solid rgba(224, 224, 224, 1)" : "none" }}>
          {firstData.label}
        </TableCell>
        <TableCell
          colSpan={2}
          sx={{
            padding: 0,
            borderBottom: data.length === 1 ? "1px solid rgba(224, 224, 224, 1)" : "none"
          }}
          className={classes.tableCellChartStyle}>
          <BarChartOverStyle
            width={`${(firstData.orverValue / firstData.max) * 100}%`}
            style={{
              left: `${(firstData.value / firstData.max) * 100}%`,
              background: basicColor
            }} />
          <BarChartStyle
            width={`${((firstData.value / firstData.max)) * 100}%`}
            style={{ backgroundColor: themeColors[3] }} />
          {isExportingPDF ? (
            <BarChartDataPointPDFStyle style={leftStyle}>
              {dataPointValue}
            </BarChartDataPointPDFStyle>
          ) : (
            <BarChartDataPointStyle style={leftStyle}>
              {dataPointValue}
            </BarChartDataPointStyle>
          )
          }
        </TableCell>
      </TableRow>
    );
  };

  // tableのrowspanの都合上、各項目の２行目からはTITableLeftRowsを使用
  const TITableLeftRows: React.FC<TITableRowProps> = ({ data, isExportingPDF }) => {
    const getLeftStyle = (argData: any): { left: string } => {
      return { left: `${((argData.value + argData.orverValue) / argData.max) * 50}%` };
    }
    const getDataPointValue = (argData: any): number => {
      return Math.round((argData.value + argData.orverValue) * 10) / 10;
    }
    return (
      <>
        {data.map((rowData: any, rowIndex: number) => (
          rowIndex > 0 && ( // data[0]は重複するのでskip
            <TableRow key={`${rowData.label}-${rowIndex}`}>
              <TableCell
                className={tableCellClassName}
                sx={{ borderBottom: rowIndex === data.length - 1 ? "1px solid rgba(224, 224, 224, 1)" : "none" }}>
                {rowData.label}
              </TableCell>
              <TableCell
                colSpan={2}
                sx={{
                  padding: 0,
                  zIndex: 5,
                  position: 'relative',
                  borderBottom: rowIndex === data.length - 1 ? "1px solid rgba(224, 224, 224, 1)" : "none"
                }}
                className={classes.tableCellChartStyle}>
                <BarChartOverStyle
                  width={`${(rowData.orverValue / rowData.max) * 100}%`}
                  style={{
                    left: `${((rowData.value / rowData.max)) * 100}%`,
                    background: basicColor
                  }} />
                <BarChartStyle
                  width={`${((rowData.value / rowData.max)) * 100}%`}
                  style={{ backgroundColor: themeColors[3] }} />
                {isExportingPDF ? (
                  <BarChartDataPointPDFStyle style={getLeftStyle(rowData)}>
                    {getDataPointValue(rowData)}
                  </BarChartDataPointPDFStyle>
                ) : (
                  <BarChartDataPointStyle style={getLeftStyle(rowData)}>
                    {getDataPointValue(rowData)}
                  </BarChartDataPointStyle>
                )
                }
              </TableCell>
            </TableRow>
          )))}
      </>
    );
  };

  const Linebreaks: React.FC<{ label: string }> = ({ label }) => {
    const [firstPart, secondPart] = label.split("(");
    return (
      <>
        {firstPart}
        {secondPart && (
          <>
            <br />
            ({secondPart}
          </>
        )}
      </>
    );
  };

  return (
    <TableContainer>
      <Table aria-label="simple table">
        <TableHead key="table-head">
          <TableRow key="table-row">
            {tableHeadList.map((item, index) => (
              <TableCell
                key={`${item.label}-${index}`}
                className={tableHeadClassName}
                sx={{ width: item.width }}
              ><Linebreaks label={item.label} />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody key="table-body">
          {displayData.map((item: TrainingIndicatorData, index: number) => (
            <React.Fragment key={`${item.group}-${index}`}>
              <TITableFirstRow
                key={`${item.group}-${index}`}
                group={item.group}
                span={item.span}
                data={item.data}
                isExportingPDF={isExportingPDF}
              />
              <TITableLeftRows
                key={`${item.data}-${index}`}
                data={item.data}
                isExportingPDF={isExportingPDF}
              />
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default IndicatorChart;